/**
 * AI Client — Integración con Moonshot Kimi K2.5 (OpenAI-compatible).
 *
 * El nombre del archivo y de la clase siguen siendo "AlibabaQwenClient" por
 * compat de imports (main.ts + AIAssistantPanel importan `qwenClient`), pero
 * internamente ahora llama al endpoint internacional de Moonshot Kimi. Si el
 * cambio se consolida, renombrar en un pase aparte.
 *
 * Endpoint: https://api.moonshot.ai/v1/chat/completions
 * Modelo:   kimi-k2.5 (latest)
 * Auth:     Bearer <VITE_MOONSHOT_API_KEY>
 *
 * Formato de request/response es OpenAI-compatible, no DashScope.
 */

import { PatientGraph, TriagePriority } from '../types/medical';
import { logger } from '../utils/Logger';

export interface AIAnalysisResult {
  differentialDiagnoses: Array<{
    condition: string;
    probability: number;
    reasoning: string;
  }>;
  recommendedTests: string[];
  triageRecommendation: TriagePriority;
  riskFactors: string[];
  confidence: number;
  /** true si el análisis proviene del generador local (sin API key). */
  isMock?: boolean;
}

export class AlibabaQwenClient {
  private apiKey: string;
  private baseURL = 'https://api.moonshot.ai/v1/chat/completions';
  private model = 'kimi-k2.5';
  private isAvailable: boolean = false;

  constructor(apiKey?: string) {
    // Vite inyecta VITE_MOONSHOT_API_KEY al build time. Fallback a la var
    // legacy VITE_ALIBABA_API_KEY por si alguien la tenía en un .env viejo.
    this.apiKey = apiKey
      || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_MOONSHOT_API_KEY)
      || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ALIBABA_API_KEY)
      || '';
    this.isAvailable = !!this.apiKey;

    if (this.isAvailable) {
      logger.info('system', `Kimi K2.5 Client inicializado (${this.model})`);
    } else {
      logger.warn('system', 'Kimi Client: No API key provided (fallback a mock local)');
    }
  }

  /**
   * Analiza un caso médico completo
   */
  async analyzeMedicalCase(patient: PatientGraph): Promise<AIAnalysisResult | null> {
    if (!this.isAvailable) {
      logger.info('system', 'Qwen sin API key — devolviendo análisis local (mock)');
      return this.generateMockAnalysis(patient);
    }

    try {
      const prompt = this.buildMedicalPrompt(patient);
      
      logger.medical('AI Analysis Requested', patient.id, {
        nodeCount: patient.nodes.length,
        chiefComplaint: patient.chiefComplaint
      });

      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // Kimi K2.5 es un modelo con reasoning interno. No acepta
          // temperature/top_p (solo admite temp=1), consume tokens en
          // `reasoning_content` antes del `content`. Por eso max_tokens alto.
          model: this.model,
          max_tokens: 6000,
          messages: [
            {
              role: 'system',
              content: `Eres un médico especialista en medicina de emergencias con 20 años de experiencia clínica.

Tu tarea es analizar datos de pacientes en triage y proporcionar:
1. Diagnósticos diferenciales ordenados por probabilidad (0-100%)
2. Estudios recomendados urgentes
3. Clasificación de triage (I=immediate/rojo, II=delayed/ámbar, III=minor/verde, IV=expectante/negro)
4. Factores de riesgo identificados
5. Nivel de confianza en el análisis

FORMATO DE RESPUESTA (JSON estricto, SIN comentarios, SIN markdown):
{
  "differentialDiagnoses": [
    {"condition": "nombre", "probability": 85, "reasoning": "explicación"}
  ],
  "recommendedTests": ["test1", "test2"],
  "triageRecommendation": "immediate|delayed|minor|expectant",
  "riskFactors": ["factor1", "factor2"],
  "confidence": 90
}

IMPORTANTE:
- "probability" y "confidence" son ENTEROS 0-100 (no decimales).
- Respondé SOLO el JSON, sin bloques de código \`\`\`json.

Reglas:
- Si hay dolor torácico + troponina elevada → Considerar IAM
- Si hay fiebre + confusión + hipotensión → Considerar sepsis
- Si hay disnea + SpO2 < 90% → Prioridad alta
- Sé conciso pero completo. Responde SOLO con el JSON.`
            },
            { role: 'user', content: prompt }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      const data = await response.json();
      // Kimi K2.5 devuelve el razonamiento en `reasoning_content` y la
      // respuesta final en `content`. Si la respuesta se corta por tokens,
      // `content` queda vacío y el JSON sólo vive en reasoning_content.
      const msg = data.choices?.[0]?.message;
      const content = msg?.content || msg?.reasoning_content;

      if (!content) {
        throw new Error('Respuesta vacía de Kimi');
      }

      return this.parseAIResponse(content, patient.id);

    } catch (error) {
      logger.error('system', 'Error en análisis Qwen', { error });
      return null;
    }
  }

  /**
   * Tutor socrático — da pistas sin revelar la respuesta
   */
  async askTutor(question: string, caseContext: string): Promise<string | null> {
    if (!this.isAvailable) {
      return this.generateMockTutorHint(caseContext);
    }

    try {
      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // Kimi K2.5 no acepta temperature custom. Max_tokens alto porque
          // gasta tokens en reasoning interno antes de responder.
          model: this.model,
          max_tokens: 2500,
          messages: [
            {
              role: 'system',
              content: `Eres un tutor de medicina de emergencias usando el método socrático.

REGLAS ESTRICTAS:
- NUNCA des la respuesta directa al diagnóstico
- Responde con PREGUNTAS que guíen al alumno hacia el razonamiento correcto
- Si el alumno está perdido, dale UNA pista sutil
- Usa máximo 2-3 oraciones
- Habla en español de Chile, tono cercano pero profesional
- Si el alumno va bien encaminado, valida y profundiza
- Relaciona siempre con fisiopatología`
            },
            {
              role: 'user',
              content: `CONTEXTO DEL CASO:\n${caseContext}\n\nPREGUNTA DEL ALUMNO: ${question}`
            }
          ]
        })
      });

      const data = await response.json();
      const msg = data.choices?.[0]?.message;
      return (msg?.content || msg?.reasoning_content || null) as string | null;
    } catch (error) {
      console.error('Tutor error:', error);
      return null;
    }
  }

  /**
   * Análisis rápido de un nodo específico
   */
  async analyzeNode(nodeData: any): Promise<string | null> {
    if (!this.isAvailable) return null;

    try {
      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: this.model,
          max_tokens: 1500,
          messages: [
            {
              role: 'system',
              content: 'Eres un asistente médico. Explica el significado clínico del dato proporcionado en 1-2 oraciones.'
            },
            {
              role: 'user',
              content: `Dato: ${nodeData.concept?.display} = ${nodeData.valueQuantity?.value || nodeData.valueString} ${nodeData.valueQuantity?.unit || ''}`
            }
          ]
        })
      });

      const data = await response.json();
      const msg = data.choices?.[0]?.message;
      return (msg?.content || msg?.reasoning_content || null) as string | null;

    } catch (error) {
      console.error('Error analyzing node:', error);
      return null;
    }
  }

  private buildMedicalPrompt(patient: PatientGraph): string {
    const vitals = patient.nodes
      .filter(n => n.type === 'vital_sign')
      .map(n => {
        const val = n.valueQuantity?.value;
        const ref = n.referenceRange;
        let flag = '';
        if (ref && val !== undefined) {
          if (ref.high && val > ref.high) flag = '⬆️ ALTO';
          else if (ref.low && val < ref.low) flag = '⬇️ BAJO';
        }
        return `- ${n.concept.display}: ${val} ${n.valueQuantity?.unit} ${flag}`;
      })
      .join('\n');

    const symptoms = patient.nodes
      .filter(n => n.type === 'symptom')
      .map(n => `- ${n.concept.display}: ${n.valueString || (n.valueBoolean ? 'Presente' : 'Ausente')} ${n.severity ? `[${n.severity}]` : ''}`)
      .join('\n');

    const labs = patient.nodes
      .filter(n => n.type === 'laboratory')
      .map(n => {
        const val = n.valueQuantity?.value;
        const ref = n.referenceRange;
        let flag = '';
        if (ref && val !== undefined) {
          if (ref.high && val > ref.high) flag = '⬆️ ELEVADO';
          else if (ref.low && val < ref.low) flag = '⬇️ DISMINUIDO';
        }
        return `- ${n.concept.display}: ${val} ${n.valueQuantity?.unit} ${flag}`;
      })
      .join('\n');

    return `PACIENTE: ${patient.id}
EDAD: ${patient.demographics.age} años
SEXO: ${patient.demographics.sex}
PESO: ${patient.demographics.weight || 'N/A'} kg

MOTIVO DE CONSULTA: ${patient.chiefComplaint || 'No especificado'}

SIGNOS VITALES:
${vitals || 'No hay signos vitales registrados'}

SÍNTOMAS:
${symptoms || 'No hay síntomas registrados'}

LABORATORIO:
${labs || 'No hay laboratorios registrados'}

TRIAGE ACTUAL: ${patient.triagePriority}

Proporciona análisis clínico estructurado.`;
  }

  private parseAIResponse(content: string, patientId: string): AIAnalysisResult | null {
    try {
      // Extraer JSON de la respuesta (puede venir con markdown)
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No se encontró JSON en respuesta');
      }

      const parsed = JSON.parse(jsonMatch[0]);

      // Kimi K2.5 a veces devuelve probability/confidence en escala 0-1
      // y a veces 0-100. Decidimos la escala mirando la lista completa:
      // si TODOS los valores están en [0,1], es decimal y reescalamos.
      // Si alguno es >1, son porcentajes ya. Evita que "1" (significando
      // 1%) se interprete como 100 cuando los otros DDx están en 0.92.
      const rawDx = parsed.differentialDiagnoses || [];
      const probs: number[] = rawDx
        .map((d: { probability?: number }) => Number(d.probability))
        .filter((n: number) => isFinite(n));
      const allDecimal = probs.length > 0 && probs.every((p: number) => p <= 1);
      const scalePct = (v: unknown): number => {
        const n = Number(v);
        if (!isFinite(n)) return 0;
        return allDecimal ? Math.round(n * 100) : Math.round(Math.min(100, n));
      };
      const scaleConfidence = (v: unknown): number => {
        const n = Number(v);
        if (!isFinite(n)) return 50;
        // Confidence es un único valor: si <=1 tratamos como decimal.
        return n <= 1 ? Math.round(n * 100) : Math.round(Math.min(100, n));
      };

      const dxs = rawDx.map((d: { condition?: string; probability?: number; reasoning?: string }) => ({
        condition: d.condition ?? 'Sin nombre',
        probability: scalePct(d.probability),
        reasoning: d.reasoning ?? '',
      }));

      const result: AIAnalysisResult = {
        differentialDiagnoses: dxs,
        recommendedTests: parsed.recommendedTests || [],
        triageRecommendation: this.validateTriage(parsed.triageRecommendation),
        riskFactors: parsed.riskFactors || [],
        confidence: scaleConfidence(parsed.confidence ?? 50),
      };

      logger.medical('AI Analysis Completed', patientId, {
        confidence: result.confidence,
        differentialCount: result.differentialDiagnoses.length
      });

      return result;

    } catch (error) {
      logger.error('system', 'Error parseando respuesta AI', { content, error });
      
      // Fallback: devolver estructura básica
      return {
        differentialDiagnoses: [{ 
          condition: 'Error en análisis AI', 
          probability: 0, 
          reasoning: content 
        }],
        recommendedTests: [],
        triageRecommendation: 'minor',
        riskFactors: [],
        confidence: 0
      };
    }
  }

  private validateTriage(triage: string): TriagePriority {
    const valid: TriagePriority[] = ['immediate', 'delayed', 'minor', 'expectant', 'deceased'];
    return valid.includes(triage as TriagePriority)
      ? (triage as TriagePriority)
      : 'minor';
  }

  /**
   * Análisis local cuando no hay API key. Inspecciona los nodos del paciente
   * y arma un diferencial razonable con umbrales clínicos simples. No reemplaza
   * al modelo, pero evita que el panel quede vacío en la demo.
   */
  private generateMockAnalysis(patient: PatientGraph): AIAnalysisResult {
    const dxs: AIAnalysisResult['differentialDiagnoses'] = [];
    const risks: string[] = [];
    const tests = new Set<string>();

    const get = (code: string) => patient.nodes.find(n => n.concept.code === code);
    const hasSymptom = (codeOrText: string) => patient.nodes.some(n =>
      n.type === 'symptom' && (n.concept.code === codeOrText || n.concept.display?.toLowerCase().includes(codeOrText))
    );

    const sbp = get('8480-6')?.valueQuantity?.value;
    const hr = get('8867-4')?.valueQuantity?.value;
    const rr = get('9279-1')?.valueQuantity?.value;
    const spo2 = get('2708-6')?.valueQuantity?.value;
    const temp = get('8310-5')?.valueQuantity?.value;
    const trop = get('10839-9')?.valueQuantity?.value ?? get('6598-7')?.valueQuantity?.value;
    const lactate = get('1968-7')?.valueQuantity?.value;

    if (hasSymptom('pecho') && typeof trop === 'number' && trop > 0.04) {
      dxs.push({
        condition: 'Síndrome coronario agudo (IAM)',
        probability: 85,
        reasoning: `Dolor torácico + troponina elevada (${trop}). Priorizar ECG 12 derivaciones.`,
      });
      tests.add('ECG 12 derivaciones');
      tests.add('Troponina seriada');
      tests.add('Rx tórax');
      risks.push('Isquemia miocárdica');
    }

    if (typeof temp === 'number' && temp >= 38 && typeof sbp === 'number' && sbp < 100) {
      const alteredMental = hasSymptom('confusión') || hasSymptom('confusion');
      const qSOFA = (typeof rr === 'number' && rr >= 22 ? 1 : 0) + (sbp <= 100 ? 1 : 0) + (alteredMental ? 1 : 0);
      if (qSOFA >= 2) {
        dxs.push({
          condition: 'Sepsis / shock séptico',
          probability: 80,
          reasoning: `qSOFA ≥ 2 (RR ${rr ?? '?'}, PAS ${sbp}, AMS ${alteredMental ? 'sí' : 'no'}). Lactato ${lactate ?? '?'}.`,
        });
        tests.add('Hemocultivos x2');
        tests.add('Lactato');
        tests.add('Hemograma + PCR');
        risks.push('Disfunción multiorgánica');
      }
    }

    if (typeof spo2 === 'number' && spo2 < 90 && typeof rr === 'number' && rr > 24) {
      dxs.push({
        condition: 'Insuficiencia respiratoria aguda',
        probability: 70,
        reasoning: `SpO₂ ${spo2}% con FR ${rr}. Descartar EPOC exacerbado, TEP, EAP.`,
      });
      tests.add('Gasometría arterial');
      tests.add('Rx tórax');
      risks.push('Hipoxemia refractaria');
    }

    if (dxs.length === 0) {
      dxs.push({
        condition: 'Sin patrón crítico evidente',
        probability: 40,
        reasoning: 'Completar anamnesis y examen físico dirigido antes de ordenar estudios.',
      });
    }

    // Triage heurístico: mantener inmediato si el triage del paciente ya lo es,
    // si no, inferir del perfil de vitales.
    let triage: TriagePriority = patient.triagePriority;
    if (triage !== 'immediate') {
      const critical =
        (typeof sbp === 'number' && sbp <= 90) ||
        (typeof spo2 === 'number' && spo2 < 88) ||
        (typeof hr === 'number' && (hr >= 130 || hr <= 40)) ||
        (typeof lactate === 'number' && lactate > 4);
      triage = critical ? 'immediate' : triage;
    }

    return {
      differentialDiagnoses: dxs.sort((a, b) => b.probability - a.probability),
      recommendedTests: Array.from(tests),
      triageRecommendation: triage,
      riskFactors: risks,
      confidence: 55,
      isMock: true,
    };
  }

  /**
   * Pista socrática local (sin API). Devuelve una pregunta guía según el
   * perfil del contexto. Intencionalmente genérica — mejor esto que nada.
   */
  private generateMockTutorHint(caseContext: string): string {
    const ctx = caseContext.toLowerCase();
    if (ctx.includes('tropon') || ctx.includes('pecho') || ctx.includes('torác')) {
      return '¿Qué signos electrocardiográficos distinguirían un STEMI de un NSTEMI en este paciente? Revisa también la cinética de la troponina.';
    }
    if (ctx.includes('lactato') || ctx.includes('fiebre') || ctx.includes('confusión')) {
      return '¿Cumple criterios de qSOFA? Piensa en foco infeccioso probable y en el tiempo hasta antibiótico empírico.';
    }
    if (ctx.includes('spo2') || ctx.includes('disnea') || ctx.includes('epoc')) {
      return '¿La hipoxemia mejora con O₂ suplementario? ¿Qué rol tiene la gasometría para diferenciar EPOC descompensado vs TEP aquí?';
    }
    return '¿Qué es lo más urgente que debes descartar en este paciente? Empieza por el ABC y fundamenta tu hipótesis antes de pedir exámenes.';
  }

  getStatus(): { available: boolean; model: string } {
    return {
      available: this.isAvailable,
      model: this.isAvailable ? this.model : 'mock-local'
    };
  }
}

// Singleton instance
export const qwenClient = new AlibabaQwenClient();
export default AlibabaQwenClient;
